import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { ToastProvider } from './components/Toast';
import { RepositoryUploadProvider } from './features/document-management/repository/upload/RepositoryUploadContext';
import { ProtectedRoute, RequirePermission } from './auth/ProtectedRoute';
import { FilesIndexPage } from './features/administration/files/pages/FilesIndexPage';
import { OrgChartPage } from './features/human-resources/organization/pages/OrgChartPage';
import { PersonnelIndexPage } from './features/human-resources/personnel/pages/PersonnelIndexPage';
import { PersonnelProfilePage } from './features/human-resources/personnel/pages/PersonnelProfilePage';
import { PersonnelSettingsPage } from './features/human-resources/personnel/pages/PersonnelSettingsPage';
import { IdentificationTypesIndexPage } from './features/human-resources/identification/pages/IdentificationTypesIndexPage';
import { LeaveTypesIndexPage } from './features/human-resources/leave/pages/LeaveTypesIndexPage';
import { LeaveTypeProfilePage } from './features/human-resources/leave/pages/LeaveTypeProfilePage';
import { IdentificationTypeProfilePage } from './features/human-resources/identification/pages/IdentificationTypeProfilePage';
import { PersonnelDocumentTypesIndexPage } from './features/human-resources/personnel-documents/pages/PersonnelDocumentTypesIndexPage';
import { PersonnelDocumentTypeProfilePage } from './features/human-resources/personnel-documents/pages/PersonnelDocumentTypeProfilePage';
import { FamilyRelationsIndexPage } from './features/human-resources/family-relations/pages/FamilyRelationsIndexPage';
import { FamilyRelationProfilePage } from './features/human-resources/family-relations/pages/FamilyRelationProfilePage';
import { PlantillaIndexPage } from './features/human-resources/plantilla/pages/PlantillaIndexPage';
import { ApplicantProfilePage } from './features/human-resources/applicants/pages/ApplicantProfilePage';
import { ApplicantRequirementProfilePage } from './features/human-resources/applicants/pages/ApplicantRequirementProfilePage';
import { AttritionRequestsIndexPage } from './features/human-resources/attrition/pages/AttritionRequestsIndexPage';
import { AttritionRequestProfilePage } from './features/human-resources/attrition/pages/AttritionRequestProfilePage';
import { AttritionSettingsPage } from './features/human-resources/attrition/pages/AttritionSettingsPage';
import { AttritionCategoriesIndexPage } from './features/human-resources/attrition-categories/pages/AttritionCategoriesIndexPage';
import { AttritionCategoryProfilePage } from './features/human-resources/attrition-categories/pages/AttritionCategoryProfilePage';
import { ApplicantRequirementsIndexPage } from './features/human-resources/applicants/pages/ApplicantRequirementsIndexPage';
import { RecruitmentSettingsPage } from './features/human-resources/applicants/pages/RecruitmentSettingsPage';
import { ScorePointsSettingsPage } from './features/human-resources/applicants/pages/ScorePointsSettingsPage';
import { JobPostingProfilePage } from './features/human-resources/applicants/pages/JobPostingProfilePage';
import { JobPostingsIndexPage } from './features/human-resources/applicants/pages/JobPostingsIndexPage';
import { DeliberationIndexPage } from './features/human-resources/deliberation/pages/DeliberationIndexPage';
import { DeliberationProfilePage } from './features/human-resources/deliberation/pages/DeliberationProfilePage';
import { VacanciesIndexPage } from './features/human-resources/vacancies/pages/VacanciesIndexPage';
import { VacancyProfilePage } from './features/human-resources/vacancies/pages/VacancyProfilePage';
import { PlantillaPositionProfilePage } from './features/human-resources/plantilla/pages/PlantillaPositionProfilePage';
import { SalaryGradesIndexPage } from './features/human-resources/plantilla/pages/SalaryGradesIndexPage';
import { SalaryTrancheProfilePage } from './features/human-resources/plantilla/pages/SalaryTrancheProfilePage';
import { OrgSettingsPage } from './features/human-resources/organization/pages/OrgSettingsPage';
import { OrgTierProfilePage } from './features/human-resources/organization/pages/OrgTierProfilePage';
import { OrgTiersPage } from './features/human-resources/organization/pages/OrgTiersPage';
import { RepositoryIndexPage } from './features/document-management/repository/pages/RepositoryIndexPage';
import { RepositorySettingsPage } from './features/document-management/repository/pages/RepositorySettingsPage';
import { InventoryItemsIndexPage } from './features/logistics/inventory/pages/InventoryItemsIndexPage';
import { InventoryItemProfilePage } from './features/logistics/inventory/pages/InventoryItemProfilePage';
import { StockBatchesIndexPage } from './features/logistics/inventory/pages/StockBatchesIndexPage';
import { StockBatchProfilePage } from './features/logistics/inventory/pages/StockBatchProfilePage';
import { StockLevelsIndexPage } from './features/logistics/inventory/pages/StockLevelsIndexPage';
import { StockMovementsIndexPage } from './features/logistics/inventory/pages/StockMovementsIndexPage';
import { StockUnitsIndexPage } from './features/logistics/inventory/pages/StockUnitsIndexPage';
import { StockUnitProfilePage } from './features/logistics/inventory/pages/StockUnitProfilePage';
import { IssuancesIndexPage } from './features/logistics/inventory/pages/IssuancesIndexPage';
import { IssuanceProfilePage } from './features/logistics/inventory/pages/IssuanceProfilePage';
import { EmployeeAssetsIndexPage } from './features/logistics/inventory/pages/EmployeeAssetsIndexPage';
import { EmployeeAssetProfilePage } from './features/logistics/inventory/pages/EmployeeAssetProfilePage';
import { InventoryAuditsIndexPage } from './features/logistics/inventory/pages/InventoryAuditsIndexPage';
import { InventoryAuditProfilePage } from './features/logistics/inventory/pages/InventoryAuditProfilePage';
import { GoodsReceiptProfilePage } from './features/logistics/inventory/pages/GoodsReceiptProfilePage';
import { GoodsReceiptsIndexPage } from './features/logistics/inventory/pages/GoodsReceiptsIndexPage';
import { RealPropertiesIndexPage } from './features/logistics/inventory/pages/RealPropertiesIndexPage';
import { RealPropertyProfilePage } from './features/logistics/inventory/pages/RealPropertyProfilePage';
import { StorageFacilitiesIndexPage } from './features/logistics/inventory/pages/StorageFacilitiesIndexPage';
import { StorageFacilityProfilePage } from './features/logistics/inventory/pages/StorageFacilityProfilePage';
import { BudgetPlanDepartmentPage } from './features/procurement/investment-planning/pages/BudgetPlanDepartmentPage';
import { BudgetPlanProfilePage } from './features/procurement/investment-planning/pages/BudgetPlanProfilePage';
import { BudgetPlansIndexPage } from './features/procurement/investment-planning/pages/BudgetPlansIndexPage';
import { BudgetReviewIndexPage } from './features/procurement/investment-planning/pages/BudgetReviewIndexPage';
import { BudgetReviewProfilePage } from './features/procurement/investment-planning/pages/BudgetReviewProfilePage';
import { BudgetAccountCodeProfilePage } from './features/procurement/investment-planning/pages/BudgetAccountCodeProfilePage';
import { BudgetAccountCodesIndexPage } from './features/procurement/investment-planning/pages/BudgetAccountCodesIndexPage';
import { BudgetDepartmentProfilePage } from './features/procurement/investment-planning/pages/BudgetDepartmentProfilePage';
import { BudgetDepartmentsIndexPage } from './features/procurement/investment-planning/pages/BudgetDepartmentsIndexPage';
import { GoalProfilePage } from './features/procurement/investment-planning/pages/GoalProfilePage';
import { GoalSectorProfilePage } from './features/procurement/investment-planning/pages/GoalSectorProfilePage';
import { GoalSectorsIndexPage } from './features/procurement/investment-planning/pages/GoalSectorsIndexPage';
import { GoalSubSectorProfilePage } from './features/procurement/investment-planning/pages/GoalSubSectorProfilePage';
import { GoalSubSectorsIndexPage } from './features/procurement/investment-planning/pages/GoalSubSectorsIndexPage';
import { GoalsIndexPage } from './features/procurement/investment-planning/pages/GoalsIndexPage';
import { InvestmentPlanningSettingsPage } from './features/procurement/investment-planning/pages/InvestmentPlanningSettingsPage';
import { PpaProfilePage } from './features/procurement/investment-planning/pages/PpaProfilePage';
import { AnnualProcurementPlanProfilePage } from './features/procurement/procurement-planning/pages/AnnualProcurementPlanProfilePage';
import { AnnualProcurementPlansIndexPage } from './features/procurement/procurement-planning/pages/AnnualProcurementPlansIndexPage';
import { AppDepartmentPage } from './features/procurement/procurement-planning/pages/AppDepartmentPage';
import { PpmpDepartmentProfilePage } from './features/procurement/procurement-planning/pages/PpmpDepartmentProfilePage';
import { PpmpDepartmentsIndexPage } from './features/procurement/procurement-planning/pages/PpmpDepartmentsIndexPage';
import { PpmpProfilePage } from './features/procurement/procurement-planning/pages/PpmpProfilePage';
import { PpmpItemProfilePage } from './features/procurement/procurement-planning/pages/PpmpItemProfilePage';
import { PpmpItemsIndexPage } from './features/procurement/procurement-planning/pages/PpmpItemsIndexPage';
import { ProcurementCatalogIndexPage } from './features/procurement/procurement-planning/pages/ProcurementCatalogIndexPage';
import { ProcurementCatalogProfilePage } from './features/procurement/procurement-planning/pages/ProcurementCatalogProfilePage';
import { ProcurementPlanningSettingsPage } from './features/procurement/procurement-planning/pages/ProcurementPlanningSettingsPage';
import { PROCUREMENT_CATALOGS } from './features/procurement/procurement-planning/procurementCatalogTypes';
import { InventorySettingsPage } from './features/logistics/inventory/pages/InventorySettingsPage';
import { InventoryCategoriesIndexPage } from './features/logistics/inventory/pages/InventoryCategoriesIndexPage';
import { InventoryCategoryProfilePage } from './features/logistics/inventory/pages/InventoryCategoryProfilePage';
import { InventoryItemTypesIndexPage } from './features/logistics/inventory/pages/InventoryItemTypesIndexPage';
import { InventoryItemTypeProfilePage } from './features/logistics/inventory/pages/InventoryItemTypeProfilePage';
import { UnitsOfMeasureIndexPage } from './features/logistics/inventory/pages/UnitsOfMeasureIndexPage';
import { UnitOfMeasureProfilePage } from './features/logistics/inventory/pages/UnitOfMeasureProfilePage';
import { ClassificationsIndexPage } from './features/document-management/repository/pages/ClassificationsIndexPage';
import { ClassificationProfilePage } from './features/document-management/repository/pages/ClassificationProfilePage';
import { DocumentProfilePage } from './features/document-management/repository/pages/DocumentProfilePage';
import { DispatchedDocumentProfilePage } from './features/document-management/routing/pages/DispatchedDocumentProfilePage';
import { DispatchedDocumentsIndexPage } from './features/document-management/routing/pages/DispatchedDocumentsIndexPage';
import { InboxIndexPage } from './features/document-management/routing/pages/InboxIndexPage';
import { InboxProfilePage } from './features/document-management/routing/pages/InboxProfilePage';
import { ReceivedDocumentProfilePage } from './features/document-management/routing/pages/ReceivedDocumentProfilePage';
import { ReceivedDocumentsIndexPage } from './features/document-management/routing/pages/ReceivedDocumentsIndexPage';
import { RoutingDocumentTypeProfilePage } from './features/document-management/routing/pages/RoutingDocumentTypeProfilePage';
import { RoutingDocumentTypesIndexPage } from './features/document-management/routing/pages/RoutingDocumentTypesIndexPage';
import { RoutingSettingsPage } from './features/document-management/routing/pages/RoutingSettingsPage';
import { EligibilitiesIndexPage } from './features/human-resources/eligibilities/pages/EligibilitiesIndexPage';
import { EligibilityProfilePage } from './features/human-resources/eligibilities/pages/EligibilityProfilePage';
import { SurveyQuestionsIndexPage } from './features/human-resources/survey/pages/SurveyQuestionsIndexPage';
import { SurveyQuestionProfilePage } from './features/human-resources/survey/pages/SurveyQuestionProfilePage';
import { CompetenciesIndexPage } from './features/human-resources/competencies/pages/CompetenciesIndexPage';
import { CompetencyProfilePage } from './features/human-resources/competencies/pages/CompetencyProfilePage';
import { LEARNING_DEVELOPMENT_CATALOGS } from './features/human-resources/learning-development/learningDevelopmentTypes';
import { LearningDevelopmentIndexPage } from './features/human-resources/learning-development/pages/LearningDevelopmentIndexPage';
import { LearningDevelopmentProfilePage } from './features/human-resources/learning-development/pages/LearningDevelopmentProfilePage';
import { PositionProfilePage } from './features/human-resources/positions/pages/PositionProfilePage';
import { PositionsIndexPage } from './features/human-resources/positions/pages/PositionsIndexPage';
import { AuditLogProfilePage } from './features/administration/audit-logs/pages/AuditLogProfilePage';
import { AuditLogsIndexPage } from './features/administration/audit-logs/pages/AuditLogsIndexPage';
import { ApprovalProfilePage } from './features/approvals/pages/ApprovalProfilePage';
import { ApprovalRoutingIndexPage } from './features/approvals/pages/ApprovalRoutingIndexPage';
import { ApprovalsIndexPage } from './features/approvals/pages/ApprovalsIndexPage';
import { PurchaseRequestProfilePage } from './features/procurement/purchasing/pages/PurchaseRequestProfilePage';
import { PurchaseOrderProfilePage } from './features/procurement/purchasing/pages/PurchaseOrderProfilePage';
import { PurchaseOrdersIndexPage } from './features/procurement/purchasing/pages/PurchaseOrdersIndexPage';
import { PurchaseRequestsIndexPage } from './features/procurement/purchasing/pages/PurchaseRequestsIndexPage';
import { RequisitionSlipsIndexPage } from './features/self-service/requisition-slips/pages/RequisitionSlipsIndexPage';
import { RequisitionSlipProfilePage } from './features/self-service/requisition-slips/pages/RequisitionSlipProfilePage';
import { EvidenceRequestsIndexPage } from './features/self-service/evidence-requests/pages/EvidenceRequestsIndexPage';
import { EvidenceRequestProfilePage } from './features/self-service/evidence-requests/pages/EvidenceRequestProfilePage';
import { CaseProfilePage } from './features/case-management/cases/pages/CaseProfilePage';
import { CasesIndexPage } from './features/case-management/cases/pages/CasesIndexPage';
import { PostOperationReportProfilePage } from './features/case-management/cases/pages/PostOperationReportProfilePage';
import { MySurveysIndexPage } from './features/self-service/surveys/pages/MySurveysIndexPage';
import { MySurveyProfilePage } from './features/self-service/surveys/pages/MySurveyProfilePage';
import { MySurveyTrainingRecommendationProfilePage } from './features/self-service/surveys/pages/MySurveyTrainingRecommendationProfilePage';
import { MyTrainingRecommendationsIndexPage } from './features/self-service/training-recommendations/pages/MyTrainingRecommendationsIndexPage';
import { MyDisbursementRequestsIndexPage } from './features/self-service/disbursement-requests/pages/MyDisbursementRequestsIndexPage';
import { MyDisbursementRequestProfilePage } from './features/self-service/disbursement-requests/pages/MyDisbursementRequestProfilePage';
import { EvidenceCasesIndexPage } from './features/evidence-management/evidence/pages/EvidenceCasesIndexPage';
import { EvidenceCaseProfilePage } from './features/evidence-management/evidence/pages/EvidenceCaseProfilePage';
import { EvidenceItemProfilePage } from './features/evidence-management/evidence/pages/EvidenceItemProfilePage';
import { EvidenceSettingsPage } from './features/evidence-management/evidence/pages/EvidenceSettingsPage';
import { EvidenceStorageLocationsIndexPage } from './features/evidence-management/evidence/pages/EvidenceStorageLocationsIndexPage';
import { EvidenceStorageLocationProfilePage } from './features/evidence-management/evidence/pages/EvidenceStorageLocationProfilePage';
import { EvidenceFileTypesPage } from './features/evidence-management/evidence/pages/EvidenceFileTypesPage';
import {
  EvidenceDisposalTypeProfilePage,
  EvidenceDisposalTypesIndexPage,
  EvidenceKindProfilePage,
  EvidenceKindsIndexPage,
  EvidenceTypeProfilePage,
  EvidenceTypesIndexPage,
} from './features/evidence-management/evidence/pages/EvidenceVocabularyPages';
import { PurchasingSettingsPage } from './features/procurement/purchasing/pages/PurchasingSettingsPage';
import { CASH_CATALOGS } from './features/cash-division/cashCatalogTypes';
import { CashCatalogIndexPage } from './features/cash-division/pages/CashCatalogIndexPage';
import { CashCatalogProfilePage } from './features/cash-division/pages/CashCatalogProfilePage';
import { CashDivisionSettingsPage } from './features/cash-division/pages/CashDivisionSettingsPage';
import { DisbursementRequestProfilePage } from './features/cash-division/pages/DisbursementRequestProfilePage';
import { DisbursementRequestsIndexPage } from './features/cash-division/pages/DisbursementRequestsIndexPage';
import { SupplierProfilePage } from './features/procurement/purchasing/pages/SupplierProfilePage';
import { AgingSettingsPage } from './features/general-services/building-management/pages/AgingSettingsPage';
import { BuildingManagementSettingsPage } from './features/general-services/building-management/pages/BuildingManagementSettingsPage';
import { MaintenanceCalendarPage } from './features/general-services/building-management/pages/MaintenanceCalendarPage';
import { MaintenanceCategoriesIndexPage } from './features/general-services/building-management/pages/MaintenanceCategoriesIndexPage';
import { MaintenanceCategoryProfilePage } from './features/general-services/building-management/pages/MaintenanceCategoryProfilePage';
import { MaintenanceIndexPage } from './features/general-services/building-management/pages/MaintenanceIndexPage';
import { MaintenanceItemProfilePage } from './features/general-services/building-management/pages/MaintenanceItemProfilePage';
import { DevelopmentPlanProfilePage } from './features/learning-development/development-plan/pages/DevelopmentPlanProfilePage';
import { DevelopmentPlansIndexPage } from './features/learning-development/development-plan/pages/DevelopmentPlansIndexPage';
import { SurveyProfilePage } from './features/learning-development/survey/pages/SurveyProfilePage';
import { SurveyPlanProfilePage } from './features/learning-development/survey/pages/SurveyPlanProfilePage';
import { SurveyTrainingRecommendationProfilePage } from './features/learning-development/survey/pages/SurveyTrainingRecommendationProfilePage';
import { SurveySettingsPage } from './features/learning-development/survey/pages/SurveySettingsPage';
import { SurveysIndexPage } from './features/learning-development/survey/pages/SurveysIndexPage';
import { PressConferenceProfilePage } from './features/public-relations/press-conferences/pages/PressConferenceProfilePage';
import { PressConferencesIndexPage } from './features/public-relations/press-conferences/pages/PressConferencesIndexPage';
import { PressReleaseProfilePage } from './features/public-relations/press-releases/pages/PressReleaseProfilePage';
import { PressReleasesIndexPage } from './features/public-relations/press-releases/pages/PressReleasesIndexPage';
import { ChannelProfilePage } from './features/public-relations/press-releases/pages/ChannelProfilePage';
import { ChannelsIndexPage } from './features/public-relations/press-releases/pages/ChannelsIndexPage';
import { PublicRelationsSettingsPage } from './features/public-relations/press-releases/pages/PublicRelationsSettingsPage';
import { TrainingRecommendationsIndexPage } from './features/learning-development/training-recommendation/pages/TrainingRecommendationsIndexPage';
import { SuppliersIndexPage } from './features/procurement/purchasing/pages/SuppliersIndexPage';
import { AccessLevelProfilePage } from './features/administration/access-levels/pages/AccessLevelProfilePage';
import { AccessLevelsIndexPage } from './features/administration/access-levels/pages/AccessLevelsIndexPage';
import { ModuleAccessLevelsPage } from './features/administration/access-levels/pages/ModuleAccessLevelsPage';
import { RoleProfilePage } from './features/administration/roles/pages/RoleProfilePage';
import { RolesIndexPage } from './features/administration/roles/pages/RolesIndexPage';
import { UserProfilePage } from './features/administration/users/pages/UserProfilePage';
import { UsersIndexPage } from './features/administration/users/pages/UsersIndexPage';
import { DashboardLayout } from './layouts/DashboardLayout';
import { firstVisibleRoute } from './layouts/sidebarConfig';
import { ChangePassword } from './pages/ChangePassword';
import { Login } from './pages/Login';
import { PermissionsChanged } from './pages/PermissionsChanged';
import { PermissionChangeWatcher } from './auth/PermissionChangeWatcher';
import { SignInCooldown } from './pages/SignInCooldown';

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        {/*
          The upload queue is mounted HERE, above the routes, and that placement is the whole
          reason it is a provider rather than state on the repository page.

          An upload of thirty megabytes outlives the reader's interest in watching it: they
          minimize the dock, carry on browsing, and it keeps going. A queue owned by the page
          would be unmounted by the first navigation, taking the batch with it.

          It renders nothing until a file is queued, so every other screen pays a context and
          no markup.
        */}
        {/*
          THE TOAST STACK, above the routes for the reason the dock below it is: the outcome
          of a write outlives the surface that made it. A modal closes on save and a form may
          navigate, so a toast owned by either would be unmounted by the thing it reports on.
        */}
        <ToastProvider>
        <RepositoryUploadProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          {/* Reached only by the sign-in page on a 429; it sends anyone else back to /login. */}
          <Route path="/cooldown" element={<SignInCooldown />} />

          {/* Outside DashboardLayout: the user must clear this before the app is reachable. */}
          <Route
            path="/change-password"
            element={
              <ProtectedRoute>
                <ChangePassword />
              </ProtectedRoute>
            }
          />

          {/*
            Outside DashboardLayout: a permission change touched the page the reader was on,
            so the sidebar and page drawn from the old set are exactly what not to show.
          */}
          <Route
            path="/permissions-changed"
            element={
              <ProtectedRoute>
                <PermissionsChanged />
              </ProtectedRoute>
            }
          />

          <Route
            path="/*"
            element={
              <ProtectedRoute>
                {/* Listens for "your permissions changed" while any page of the app is open. */}
                <PermissionChangeWatcher />
                <DashboardLayout>
                  <Routes>
                    <Route index element={<LandingRedirect />} />
                    {/*
                      The folder being browsed lives in the query string (`?folder=<id>`)
                      rather than in the path, so one route serves every level of the tree —
                      a folder is still linkable, the back button still works, and a reload
                      lands where the reader was.
                    */}
                    <Route
                      path="repository"
                      element={
                        <RequirePermission permission="repository.read">
                          <RepositoryIndexPage />
                        </RequirePermission>
                      }
                    />
                    {/*
                      A DOCUMENT gets a profile; a folder does not — a folder is a place, and
                      its anchor opens its contents. Product call, 15 Sep 2026.
                    */}
                    <Route
                      path="repository/documents/:id"
                      element={
                        <RequirePermission permission="repository.read">
                          <DocumentProfilePage />
                        </RequirePermission>
                      }
                    />
                    {/*
                      Settings, and the catalogs it leads to. A catalog is reached THROUGH this
                      menu and nowhere else, so its path nests under it — the breadcrumb is the
                      back-nav, and a top-level path would leave it unable to say where the
                      reader came from.
                    */}
                    <Route
                      path="repository/settings"
                      element={
                        <RequirePermission permission="repository.read">
                          <RepositorySettingsPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="repository/settings/classifications"
                      element={
                        <RequirePermission permission="repository.read">
                          <ClassificationsIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="repository/settings/classifications/:id"
                      element={
                        <RequirePermission permission="repository.read">
                          <ClassificationProfilePage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      DOCUMENT MANAGEMENT › ROUTING. Two sibling modules, each on its own path —
                      a group of modules groups navigation, not routes. The document-type catalog
                      is reached through Receiving's Settings and nests there. The list sits at
                      `/documents` so the rail, which lights by prefix, does not light Receiving
                      on the Settings page; the bare path redirects so a bookmark lands somewhere.
                    */}
                    <Route path="document-receiving" element={<Navigate to="/document-receiving/documents" replace />} />
                    <Route
                      path="document-receiving/documents"
                      element={
                        <RequirePermission permission="document-receiving.read">
                          <ReceivedDocumentsIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="document-receiving/documents/:id"
                      element={
                        <RequirePermission permission="document-receiving.read">
                          <ReceivedDocumentProfilePage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="document-receiving/settings"
                      element={
                        <RequirePermission permission="routing-document-types.read">
                          <RoutingSettingsPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="document-receiving/settings/document-types"
                      element={
                        <RequirePermission permission="routing-document-types.read">
                          <RoutingDocumentTypesIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="document-receiving/settings/document-types/:id"
                      element={
                        <RequirePermission permission="routing-document-types.read">
                          <RoutingDocumentTypeProfilePage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="document-dispatching"
                      element={
                        <RequirePermission permission="document-dispatching.read">
                          <DispatchedDocumentsIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="document-dispatching/:id"
                      element={
                        <RequirePermission permission="document-dispatching.read">
                          <DispatchedDocumentProfilePage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="document-inbox"
                      element={
                        <RequirePermission permission="document-inbox.read">
                          <InboxIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="document-inbox/:id"
                      element={
                        <RequirePermission permission="document-inbox.read">
                          <InboxProfilePage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      Logistics › Inventory.

                      The bare path redirects rather than rendering a blank branch route — a
                      bookmark on /inventory has to land somewhere, and that is the group's
                      FIRST rail entry. It pointed at Items until 18 Sep 2026; Items is behind
                      Settings now, so the landing moved with the rail rather than sending every
                      bookmark two levels in. From 08 Oct 2026 the first entry is Monitoring ›
                      Stock Levels, and the landing moved with it again.
                    */}
                    <Route path="inventory" element={<Navigate to="/inventory/stock-levels" replace />} />

                    {/*
                      ITEMS is reached through Logistics → Settings, so its path says where the
                      reader came from — the rule every settings-nested module follows, and what
                      lets the breadcrumb offer a way back.

                      The OLD paths redirect rather than 404ing. Items was a sidebar row for the
                      life of the module, which puts it in history and in bookmarks, and the
                      profile path is what `/inventory/stock-levels` anchors opened until this
                      change. `:id` is carried across, or a bookmarked record would land on the
                      list and say nothing about why.
                    */}
                    <Route path="inventory/items" element={<Navigate to="/inventory/settings/items" replace />} />
                    <Route path="inventory/items/:id" element={<RedirectItemProfile />} />

                    <Route
                      path="inventory/settings/items"
                      element={
                        <RequirePermission permission="inventory-items.read">
                          <InventoryItemsIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="inventory/settings/items/:id"
                      element={
                        <RequirePermission permission="inventory-items.read">
                          <InventoryItemProfilePage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      Gated on the module's OWN read key, and gated twice — here and on the
                      sidebar entry. Either alone is a defect: a gated nav over an open route is
                      still reachable by typing the URL, and a gated route under open nav is a
                      menu item that bounces the reader back to the landing page.
                    */}
                    <Route
                      path="inventory/stock-batches"
                      element={
                        <RequirePermission permission="stock-batches.read">
                          <StockBatchesIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="inventory/stock-batches/:id"
                      element={
                        <RequirePermission permission="stock-batches.read">
                          <StockBatchProfilePage />
                        </RequirePermission>
                      }
                    />

                    <Route
                      path="inventory/stock-units"
                      element={
                        <RequirePermission permission="stock-units.read">
                          <StockUnitsIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="inventory/stock-units/:id"
                      element={
                        <RequirePermission permission="stock-units.read">
                          <StockUnitProfilePage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      ONE route, and no `:id` beside it. A level is DERIVED — a view of an item
                      rather than a record — so there is nothing to open a profile of, and the
                      row anchor opens the item instead.
                    */}
                    <Route
                      path="inventory/stock-levels"
                      element={
                        <RequirePermission permission="stock-levels.read">
                          <StockLevelsIndexPage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      ONE route here too, and for a different reason: a ledger LINE is a stored
                      record, but its whole content fits on the row — so the anchor opens the
                      ITEM whose stock moved rather than a profile of its own. See the page.
                    */}
                    <Route
                      path="inventory/stock-movements"
                      element={
                        <RequirePermission permission="stock-movements.read">
                          <StockMovementsIndexPage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      A PROFILE here, unlike the two routes above it — an issuance is a stored
                      record whose content does not fit on its row: the lines it released, the
                      lots each line drew from, and every return recorded against it.

                      Both routes carry `issuances.read`, the read key of the module they open.
                      Opening a record is not changing it, and the Issue form behind the list
                      carries `issuances.create` where it is drawn.
                    */}
                    <Route
                      path="inventory/issuances"
                      element={
                        <RequirePermission permission="issuances.read">
                          <IssuancesIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="inventory/issuances/:id"
                      element={
                        <RequirePermission permission="issuances.read">
                          <IssuanceProfilePage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      EMPLOYEE ASSETS — every employee, and a profile behind each holding the
                      history of what was issued to them. Both routes carry the module's one key;
                      issuing and returning carry the issuance register's keys where they are drawn.
                    */}
                    <Route
                      path="inventory/employee-assets"
                      element={
                        <RequirePermission permission="employee-assets.read">
                          <EmployeeAssetsIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="inventory/employee-assets/:id"
                      element={
                        <RequirePermission permission="employee-assets.read">
                          <EmployeeAssetProfilePage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      A PROFILE here too, for the same reason: an audit's content is a count sheet
                      of a few hundred rows, which is not something a list row can carry.

                      Both routes carry `inventory-audits.read` — opening a record is not changing
                      it. Starting, counting and completing each carry their own key where they
                      are drawn, because they are three different jobs.
                    */}
                    <Route
                      path="inventory/inventory-audits"
                      element={
                        <RequirePermission permission="inventory-audits.read">
                          <InventoryAuditsIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="inventory/inventory-audits/:id"
                      element={
                        <RequirePermission permission="inventory-audits.read">
                          <InventoryAuditProfilePage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      GOODS RECEIPTS — the seventh work surface, and a profile behind its anchor
                      because a delivery's content is its lines and what each one was found to be.

                      Both routes carry `goods-receipts.read` — opening a record is not changing
                      it. Recording, inspecting and registering each carry their own key where
                      they are drawn, because they are three different jobs.
                    */}
                    <Route
                      path="inventory/goods-receipts"
                      element={
                        <RequirePermission permission="goods-receipts.read">
                          <GoodsReceiptsIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="inventory/goods-receipts/:id"
                      element={
                        <RequirePermission permission="goods-receipts.read">
                          <GoodsReceiptProfilePage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      Storage Facilities moved under Settings, so the old path REDIRECTS rather
                      than 404s — it was a sidebar entry, which puts it in history and bookmarks.
                      The same courtesy Organization's retired Manage route gets.
                    */}
                    <Route
                      path="inventory/storage-facilities"
                      element={<Navigate to="/inventory/settings/storage-facilities" replace />}
                    />
                    <Route
                      path="inventory/storage-facilities/:id"
                      element={<Navigate to="/inventory/settings/storage-facilities" replace />}
                    />
                    {/*
                      Settings is a menu over surfaces belonging to four DIFFERENT modules, so it
                      is gated on holding ANY of their keys — one of them would hide the page
                      from a reader entitled to another. Each surface below carries its own.

                      All four nest under this path and exist nowhere else. For the three
                      vocabularies that is what keeps `inventory-items.read`'s unscoped implied
                      read honest: a catalog reached only through a menu has no surface for the
                      grant to arrive at. For Storage Facilities it is the breadcrumb — a
                      top-level path would leave it unable to say where the reader came from.
                    */}
                    <Route
                      path="inventory/settings"
                      element={
                        <RequirePermission
                          anyOf={[
                            'inventory-categories.read',
                            'inventory-item-types.read',
                            'units-of-measure.read',
                            'storage-facilities.read',
                          ]}
                        >
                          <InventorySettingsPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="inventory/settings/categories"
                      element={
                        <RequirePermission permission="inventory-categories.read">
                          <InventoryCategoriesIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="inventory/settings/categories/:id"
                      element={
                        <RequirePermission permission="inventory-categories.read">
                          <InventoryCategoryProfilePage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="inventory/settings/item-types"
                      element={
                        <RequirePermission permission="inventory-item-types.read">
                          <InventoryItemTypesIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="inventory/settings/item-types/:id"
                      element={
                        <RequirePermission permission="inventory-item-types.read">
                          <InventoryItemTypeProfilePage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="inventory/settings/units"
                      element={
                        <RequirePermission permission="units-of-measure.read">
                          <UnitsOfMeasureIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="inventory/settings/units/:id"
                      element={
                        <RequirePermission permission="units-of-measure.read">
                          <UnitOfMeasureProfilePage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="inventory/settings/real-properties"
                      element={
                        <RequirePermission permission="real-properties.read">
                          <RealPropertiesIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="inventory/settings/real-properties/:id"
                      element={
                        <RequirePermission permission="real-properties.read">
                          <RealPropertyProfilePage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="inventory/settings/storage-facilities"
                      element={
                        <RequirePermission permission="storage-facilities.read">
                          <StorageFacilitiesIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="inventory/settings/storage-facilities/:id"
                      element={
                        <RequirePermission permission="storage-facilities.read">
                          <StorageFacilityProfilePage />
                        </RequirePermission>
                      }
                    />

                    <Route
                      path="users"
                      element={
                        <RequirePermission permission="users.read">
                          <UsersIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="users/:id"
                      element={
                        <RequirePermission permission="users.read">
                          <UserProfilePage />
                        </RequirePermission>
                      }
                    />
                    {/*
                      THE CLEARANCE LADDER. Its own path rather than one nested under
                      `/users`, because the rail draws an active bar by PREFIX: a child at
                      `/users/access-levels` would light the Users row too, which is the defect
                      the approvals inbox already shipped once.
                    */}
                    <Route
                      path="access-levels"
                      element={
                        <RequirePermission permission="access-levels.read">
                          <AccessLevelsIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="access-levels/:id"
                      element={
                        <RequirePermission permission="access-levels.read">
                          <AccessLevelProfilePage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="roles"
                      element={
                        <RequirePermission permission="roles.read">
                          <RolesIndexPage />
                        </RequirePermission>
                      }
                    />
                    {/*
                      A role's own page: what it is, and exactly what it grants. Gated on the
                      same key as the list, per "a page is gated twice, with the same key" —
                      the sidebar entry above covers the nav half for both.
                    */}
                    <Route
                      path="roles/:id"
                      element={
                        <RequirePermission permission="roles.read">
                          <RoleProfilePage />
                        </RequirePermission>
                      }
                    />
                    {/*
                      Organization is three surfaces over one chart. The bare path is not one
                      of them: it redirects to Preview, so a bookmark on /organization still
                      lands somewhere rather than on a blank branch route.
                    */}
                    <Route path="organization" element={<Navigate to="/organization/chart" replace />} />
                    <Route
                      path="organization/chart"
                      element={
                        <RequirePermission permission="org-units.read">
                          <OrgChartPage />
                        </RequirePermission>
                      }
                    />
                    {/*
                      There is one chart surface now, and it is Chart. The read-only copy is
                      gone and Manage is one of two paths it used to live at, so both redirect
                      rather than 404 — each was a rail entry, which puts it in history and
                      bookmarks.
                    */}
                    <Route
                      path="organization/manage"
                      element={<Navigate to="/organization/chart" replace />}
                    />
                    {/*
                      Preview was what this surface was called until 20 Sep 2026, when it took the
                      name of what it IS. It was a rail entry for the whole of that time, so it is in
                      people's history and their bookmarks — a redirect rather than a 404.
                    */}
                    <Route
                      path="organization/preview"
                      element={<Navigate to="/organization/chart" replace />}
                    />
                    {/*
                      Settings is a menu over surfaces belonging to different modules, so it
                      is gated on holding ANY of their keys — one of them would hide the page
                      from a caller entitled to the other. Each surface below still carries
                      its own.
                    */}
                    <Route
                      path="organization/settings"
                      element={
                        <RequirePermission
                          anyOf={['org-unit-types.read', 'positions.read', 'salary-grades.read']}
                        >
                          <OrgSettingsPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="organization/settings/tiers"
                      element={
                        <RequirePermission permission="org-unit-types.read">
                          <OrgTiersPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="organization/settings/tiers/:id"
                      element={
                        <RequirePermission permission="org-unit-types.read">
                          <OrgTierProfilePage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="organization/settings/positions"
                      element={
                        <RequirePermission permission="positions.read">
                          <PositionsIndexPage />
                        </RequirePermission>
                      }
                    />
                    {/*
                      The profile nests under the catalog's own path, because it is a second
                      surface over the same records rather than a module of its own.
                    */}
                    <Route
                      path="organization/settings/positions/:id"
                      element={
                        <RequirePermission permission="positions.read">
                          <PositionProfilePage />
                        </RequirePermission>
                      }
                    />
                    {/*
                      Eligibilities moved to Personnel → Settings: positions were the only thing
                      binding the catalog when it was filed here, and CS Form 212 section IV made
                      personnel the second consumer and the heavier one. The old path redirects
                      rather than 404s — it was a Settings row, so it is in history and bookmarks.
                    */}
                    <Route
                      path="organization/settings/eligibilities"
                      element={<Navigate to="/personnel/settings/eligibilities" replace />}
                    />
                    {/*
                      Tiers moved under Organization settings, twice — first off the sidebar,
                      then off the Settings page itself onto their own route. Both old paths
                      redirect rather than 404: each was somewhere people navigated to, so
                      each is in their history and their bookmarks.
                    */}
                    <Route
                      path="unit-types"
                      element={<Navigate to="/organization/settings/tiers" replace />}
                    />
                    {/*
                      The bare path redirects rather than rendering a blank branch route: a
                      bookmark on /personnel has to land somewhere, and the first child is where.
                    */}
                    <Route path="personnel" element={<Navigate to="/personnel/201-files" replace />} />
                    <Route
                      path="personnel/201-files"
                      element={
                        <RequirePermission permission="personnel.read">
                          <PersonnelIndexPage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      The RETIRED paths, BOTH kept. This surface was `Preview` for the life of
                      the module, then `Data Sheet` from 19 Sep to 22 Oct 2026, so both sit in
                      history and in bookmarks and both redirect rather than 404ing — what every
                      renamed route here does.

                      A second rename does not retire the first redirect: whoever bookmarked
                      `/personnel/preview` never visited `/personnel/data-sheet`, so dropping it
                      would 404 exactly the oldest bookmark. They point at the CURRENT path, not
                      at each other — a chain of redirects is a round trip per hop.

                      No `:id` twin is needed: the profile is `/personnel/:id` and did not move.
                    */}
                    <Route
                      path="personnel/preview"
                      element={<Navigate to="/personnel/201-files" replace />}
                    />
                    <Route
                      path="personnel/data-sheet"
                      element={<Navigate to="/personnel/201-files" replace />}
                    />
                    <Route
                      path="personnel/settings"
                      element={
                        <RequirePermission
                          anyOf={[
                            'identification-types.read',
                            'family-relations.read',
                            'eligibilities.read',
                            'survey-questions.read',
                            'competencies.read',
                            'learning-development.read',
                            'personnel-document-types.read',
                            'personnel.manage-catalogs',
                            'users.assign-levels',
                          ]}
                        >
                          <PersonnelSettingsPage />
                        </RequirePermission>
                      }
                    />
                    {/*
                      WHO READS WHAT — the roster mirroring user management inside Personnel.

                      Gated on `users.assign-levels`, which is what the page DOES. The panel
                      inside it also tests `users.read`, because its rows are accounts: tagging
                      a clearance and enumerating every account in the building are different
                      grants, and the surface owes both.
                    */}
                    <Route
                      path="personnel/settings/access-levels"
                      element={
                        <RequirePermission permission="users.assign-levels">
                          <ModuleAccessLevelsPage
                            moduleKey="personnel"
                            moduleName="Personnel"
                            breadcrumb={[
                              'Human Resources',
                              'Personnel',
                              { label: 'Settings', to: '/personnel/settings' },
                              'Access Levels',
                            ]}
                          />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="personnel/settings/leave-types"
                      element={
                        <RequirePermission permission="leave-types.read">
                          <LeaveTypesIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="personnel/settings/leave-types/:id"
                      element={
                        <RequirePermission permission="leave-types.read">
                          <LeaveTypeProfilePage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="personnel/settings/identification"
                      element={
                        <RequirePermission permission="identification-types.read">
                          <IdentificationTypesIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="personnel/settings/identification/:id"
                      element={
                        <RequirePermission permission="identification-types.read">
                          <IdentificationTypeProfilePage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="personnel/settings/documents"
                      element={
                        <RequirePermission permission="personnel-document-types.read">
                          <PersonnelDocumentTypesIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="personnel/settings/documents/:id"
                      element={
                        <RequirePermission permission="personnel-document-types.read">
                          <PersonnelDocumentTypeProfilePage />
                        </RequirePermission>
                      }
                    />
                    {/*
                      Nested under Personnel → Settings rather than given a top-level path: that
                      menu is the only way to this catalog, so the path has to say where the
                      reader came from or the breadcrumb cannot offer a way back.
                    */}
                    <Route
                      path="personnel/settings/family-relations"
                      element={
                        <RequirePermission permission="family-relations.read">
                          <FamilyRelationsIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="personnel/settings/family-relations/:id"
                      element={
                        <RequirePermission permission="family-relations.read">
                          <FamilyRelationProfilePage />
                        </RequirePermission>
                      }
                    />
                    {/*
                      Its own module and its own keys, nested here because this menu is the way
                      to it — the same shape Identification and Family Relations take.
                    */}
                    <Route
                      path="personnel/settings/eligibilities"
                      element={
                        <RequirePermission permission="eligibilities.read">
                          <EligibilitiesIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="personnel/settings/eligibilities/:id"
                      element={
                        <RequirePermission permission="eligibilities.read">
                          <EligibilityProfilePage />
                        </RequirePermission>
                      }
                    />
                    {/*
                      The survey questionnaire — its own module and its own keys, nested here
                      because this menu is the way to it, exactly as Identification, Family
                      Relations and Eligibilities are. The ANSWERS are not here: they are a
                      section of the 201 file and live on the personnel profile, where the panel
                      lists every question and records one answer against each.
                    */}
                    <Route
                      path="personnel/settings/survey"
                      element={
                        <RequirePermission permission="survey-questions.read">
                          <SurveyQuestionsIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="personnel/settings/survey/:id"
                      element={
                        <RequirePermission permission="survey-questions.read">
                          <SurveyQuestionProfilePage />
                        </RequirePermission>
                      }
                    />
                    {/*
                      The competency framework — its own module, reached only through Personnel →
                      Settings, so it takes that menu's path exactly as Survey and Eligibilities
                      do. The L&D RECORDS are not here: they are a section of the 201 file and
                      live on the personnel profile's Training/L&D panel.
                    */}
                    <Route
                      path="personnel/settings/competencies"
                      element={
                        <RequirePermission permission="competencies.read">
                          <CompetenciesIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="personnel/settings/competencies/:id"
                      element={
                        <RequirePermission permission="competencies.read">
                          <CompetencyProfilePage />
                        </RequirePermission>
                      }
                    />
                    {/*
                      The four L&D vocabularies — one pair of routes each, generated from the
                      descriptor rather than written out four times. Every one is reached only
                      through Personnel → Settings, so each takes that menu's path exactly as
                      Competencies and Survey do: the path has to say where the reader came
                      from, or the breadcrumb cannot offer a way back.

                      ONE key across all four, `learning-development.read`, because maintaining
                      these vocabularies is one administrative job. The RECORDS they describe
                      are a section of the 201 file and live on the personnel profile's
                      Training/L&D panel, under `personnel.update`.
                    */}
                    {LEARNING_DEVELOPMENT_CATALOGS.flatMap((catalog) => [
                      <Route
                        key={catalog.slug}
                        path={catalog.settingsPath.replace(/^\//, '')}
                        element={
                          <RequirePermission permission="learning-development.read">
                            <LearningDevelopmentIndexPage catalog={catalog} />
                          </RequirePermission>
                        }
                      />,
                      <Route
                        key={`${catalog.slug}-profile`}
                        path={`${catalog.settingsPath.replace(/^\//, '')}/:id`}
                        element={
                          <RequirePermission permission="learning-development.read">
                            <LearningDevelopmentProfilePage catalog={catalog} />
                          </RequirePermission>
                        }
                      />,
                    ])}
                    <Route
                      path="personnel/:id"
                      element={
                        <RequirePermission permission="personnel.read">
                          <PersonnelProfilePage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="plantilla"
                      element={
                        <RequirePermission permission="plantilla.read">
                          <PlantillaIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="plantilla/:id"
                      element={
                        <RequirePermission permission="plantilla.read">
                          <PlantillaPositionProfilePage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="vacancies"
                      element={
                        <RequirePermission permission="vacancies.read">
                          <VacanciesIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="vacancies/:id"
                      element={
                        <RequirePermission permission="vacancies.read">
                          <VacancyProfilePage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="applicants"
                      element={
                        <RequirePermission permission="applicants.read">
                          <JobPostingsIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="applicants/:postingId"
                      element={
                        <RequirePermission permission="applicants.read">
                          <JobPostingProfilePage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="applicants/:postingId/:applicantId"
                      element={
                        <RequirePermission permission="applicants.read">
                          <ApplicantProfilePage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="deliberation"
                      element={
                        <RequirePermission permission="deliberations.read">
                          <DeliberationIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="deliberation/:postingId"
                      element={
                        <RequirePermission permission="deliberations.read">
                          <DeliberationProfilePage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="recruitment/settings"
                      element={
                        // ANY of its rows' keys; a row added owes its key here too.
                        <RequirePermission anyOf={['applicant-requirements.read', 'applicants.read']}>
                          <RecruitmentSettingsPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="recruitment/settings/score-points"
                      element={
                        <RequirePermission permission="applicants.read">
                          <ScorePointsSettingsPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="recruitment/settings/applicant-requirements"
                      element={
                        <RequirePermission permission="applicant-requirements.read">
                          <ApplicantRequirementsIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="recruitment/settings/applicant-requirements/:id"
                      element={
                        <RequirePermission permission="applicant-requirements.read">
                          <ApplicantRequirementProfilePage />
                        </RequirePermission>
                      }
                    />
                    {/* Attrition — the bare path lands on Requests; Settings nests, being reached only that way. */}
                    <Route path="attrition" element={<Navigate to="/attrition/requests" replace />} />
                    <Route
                      path="attrition/requests"
                      element={
                        <RequirePermission permission="attrition-requests.read">
                          <AttritionRequestsIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="attrition/requests/:id"
                      element={
                        <RequirePermission permission="attrition-requests.read">
                          <AttritionRequestProfilePage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="attrition/settings"
                      element={
                        // ANY of its rows' keys; a row added owes its key here too.
                        <RequirePermission anyOf={['attrition-categories.read']}>
                          <AttritionSettingsPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="attrition/settings/categories"
                      element={
                        <RequirePermission permission="attrition-categories.read">
                          <AttritionCategoriesIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="attrition/settings/categories/:id"
                      element={
                        <RequirePermission permission="attrition-categories.read">
                          <AttritionCategoryProfilePage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="organization/settings/salary-grades"
                      element={
                        <RequirePermission permission="salary-grades.read">
                          <SalaryGradesIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="organization/settings/salary-grades/:type/:tranche"
                      element={
                        <RequirePermission permission="salary-grades.read">
                          <SalaryTrancheProfilePage />
                        </RequirePermission>
                      }
                    />
                    {/*
                      The schedule moved into Organization settings. The old path redirects
                      rather than 404s: it was a sidebar entry, so it is in people's history
                      and their bookmarks — the same treatment Tiers got.
                    */}
                    <Route
                      path="salary-grades"
                      element={<Navigate to="/organization/settings/salary-grades" replace />}
                    />
                    <Route
                      path="files"
                      element={
                        <RequirePermission permission="files.read">
                          <FilesIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="audit-logs"
                      element={
                        <RequirePermission permission="audit-logs.read">
                          <AuditLogsIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="audit-logs/:id"
                      element={
                        <RequirePermission permission="audit-logs.read">
                          <AuditLogProfilePage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      APPROVALS — the platform's approval engine, filed under Administration
                      because that is where its surfaces are, the same reading that puts the
                      audit trail here while its interceptor is platform code owning nobody.

                      BOTH SURFACES NEST, and the bare path REDIRECTS to the first child. That
                      is the Organization pattern, and it is not cosmetic: with the inbox at
                      `/approvals` and the routing at `/approvals/routing`, the rail matched the
                      inbox's row as a PREFIX of its sibling's and drew the active indicator on
                      BOTH rows at once. Found by looking at the screen — it is type-correct and
                      lint-clean either way.

                      A bookmark on `/approvals` still has to land somewhere, which the redirect
                      is for.
                    */}
                    <Route path="approvals" element={<Navigate to="/approvals/requests" replace />} />
                    <Route
                      path="approvals/requests"
                      element={
                        <RequirePermission permission="approvals.read">
                          <ApprovalsIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="approvals/requests/:id"
                      element={
                        <RequirePermission permission="approvals.read">
                          <ApprovalProfilePage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="approvals/routing"
                      element={
                        <RequirePermission permission="approval-categories.read">
                          <ApprovalRoutingIndexPage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      PROCUREMENT › INVESTMENT PLANNING.

                      The group's bare path redirects to its first child rather than rendering
                      a blank branch route — a bookmark on it has to land somewhere. Today that
                      child is Settings, because the four work surfaces (Budget Plan, Budget
                      Review, Allotment Release, Obligations) are not built yet; when Budget
                      Plans lands, this redirect moves to it.

                      Every catalog below nests under `settings/` and exists nowhere else: the
                      breadcrumb is the back-nav, so a top-level path would leave each page
                      unable to say where the reader came from.
                    */}
                    <Route
                      path="procurement"
                      element={<Navigate to="/procurement/investment-planning/budget-plans" replace />}
                    />
                    <Route
                      path="procurement/investment-planning"
                      element={<Navigate to="/procurement/investment-planning/budget-plans" replace />}
                    />

                    {/*
                      The group's first WORK surface, and what its bare path now redirects to.
                      It pointed at Settings while nothing else existed; a bookmark on the group
                      should land on the work rather than on the vocabulary behind it.
                    */}
                    <Route
                      path="procurement/investment-planning/budget-plans"
                      element={
                        <RequirePermission permission="budget-plans.read">
                          <BudgetPlansIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="procurement/investment-planning/budget-plans/:id"
                      element={
                        <RequirePermission permission="budget-plans.read">
                          <BudgetPlanProfilePage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      One department's slice of one plan, nested under the plan because that
                      is where the reader came from and the breadcrumb has to be able to say
                      so. `budget-plans.read`, not `ppas.read`: this is still the plan's own
                      roster read one level in, which is the same call the roster ENDPOINT
                      takes. Opening a PPA is the narrower question and carries the other key.
                    */}
                    <Route
                      path="procurement/investment-planning/budget-plans/:planId/departments/:orgUnitId"
                      element={
                        <RequirePermission permission="budget-plans.read">
                          <BudgetPlanDepartmentPage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      BUDGET REVIEW — the group's second work surface, appended after Budget
                      Plans in the cycle's own order. It lists the SAME records the plan list
                      does, narrowed to the stages the review can act on, which is why its
                      profile reads the plan and its roster rather than a record of its own.
                    */}
                    <Route
                      path="procurement/investment-planning/budget-review"
                      element={
                        <RequirePermission permission="budget-review.read">
                          <BudgetReviewIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="procurement/investment-planning/budget-review/:id"
                      element={
                        <RequirePermission permission="budget-review.read">
                          <BudgetReviewProfilePage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      The PPA profile is the ONE route `ppas.read` gates, and the module still
                      has no index page and no rail row — every list of PPAs is scoped to a
                      plan or a department. So "a page is gated twice, with the same key" has
                      only its route half here: there is no sidebar entry to carry the other.
                    */}
                    <Route
                      path="procurement/investment-planning/ppas/:id"
                      element={
                        <RequirePermission permission="ppas.read">
                          <PpaProfilePage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      Settings is a menu over surfaces belonging to THREE different modules, so
                      it is gated on holding ANY of their keys — one of them would hide the page
                      from a reader entitled to another. A row added to the menu owes its key
                      here in the same change, or the page hides itself from that row's reader.
                    */}
                    <Route
                      path="procurement/investment-planning/settings"
                      element={
                        <RequirePermission
                          anyOf={[
                            'budget-goals.read',
                            'budget-account-codes.read',
                            'budget-departments.read',
                          ]}
                        >
                          <InvestmentPlanningSettingsPage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      The goal hierarchy — three rungs, one key set. Three routes rather than
                      one parameterised page, because the rungs have different columns.
                    */}
                    <Route
                      path="procurement/investment-planning/settings/goal-sectors"
                      element={
                        <RequirePermission permission="budget-goals.read">
                          <GoalSectorsIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="procurement/investment-planning/settings/goal-sectors/:id"
                      element={
                        <RequirePermission permission="budget-goals.read">
                          <GoalSectorProfilePage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="procurement/investment-planning/settings/goal-sub-sectors"
                      element={
                        <RequirePermission permission="budget-goals.read">
                          <GoalSubSectorsIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="procurement/investment-planning/settings/goal-sub-sectors/:id"
                      element={
                        <RequirePermission permission="budget-goals.read">
                          <GoalSubSectorProfilePage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="procurement/investment-planning/settings/goals"
                      element={
                        <RequirePermission permission="budget-goals.read">
                          <GoalsIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="procurement/investment-planning/settings/goals/:id"
                      element={
                        <RequirePermission permission="budget-goals.read">
                          <GoalProfilePage />
                        </RequirePermission>
                      }
                    />

                    <Route
                      path="procurement/investment-planning/settings/account-codes"
                      element={
                        <RequirePermission permission="budget-account-codes.read">
                          <BudgetAccountCodesIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="procurement/investment-planning/settings/account-codes/:id"
                      element={
                        <RequirePermission permission="budget-account-codes.read">
                          <BudgetAccountCodeProfilePage />
                        </RequirePermission>
                      }
                    />

                    <Route
                      path="procurement/investment-planning/settings/budget-departments"
                      element={
                        <RequirePermission permission="budget-departments.read">
                          <BudgetDepartmentsIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="procurement/investment-planning/settings/budget-departments/:id"
                      element={
                        <RequirePermission permission="budget-departments.read">
                          <BudgetDepartmentProfilePage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      PROCUREMENT PLANNING — the suite's second module group, and every surface
                      it has so far is a vocabulary behind Settings. The bare path redirects
                      there rather than rendering a blank branch route, because a bookmark on
                      it has to land somewhere; it re-points at the first WORK surface the day
                      one lands.
                    */}
                    <Route
                      path="procurement/procurement-planning"
                      element={
                        /*
                          THE FIRST CHILD, which is now a work surface rather than Settings —
                          the group gained one, and a bare path redirects to the first child
                          rather than to wherever it used to land.
                        */
                        <Navigate
                          to="/procurement/procurement-planning/annual-procurement-plans"
                          replace
                        />
                      }
                    />
                    <Route
                      path="procurement/procurement-planning/settings"
                      element={
                        <RequirePermission
                          anyOf={['procurement-catalogs.read', 'ppmp-items.read']}
                        >
                          <ProcurementPlanningSettingsPage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      THE TWO FLAT VOCABULARIES ARE ONE PAIR OF PAGES, parameterised by the
                      descriptor — and the ROUTES are derived from the same array the Settings
                      rows and the pages read, so a third vocabulary cannot appear in the menu
                      with no route behind it. The paths are absolute on the descriptor because
                      that is how every link states them; these routes sit under a parent, so
                      the leading slash comes off.
                    */}
                    {PROCUREMENT_CATALOGS.map((catalog) => (
                      <Route key={catalog.slug} path={catalog.settingsPath.slice(1)}>
                        <Route
                          index
                          element={
                            <RequirePermission permission="procurement-catalogs.read">
                              <ProcurementCatalogIndexPage catalog={catalog} />
                            </RequirePermission>
                          }
                        />
                        <Route
                          path=":id"
                          element={
                            <RequirePermission permission="procurement-catalogs.read">
                              <ProcurementCatalogProfilePage catalog={catalog} />
                            </RequirePermission>
                          }
                        />
                      </Route>
                    ))}

                    <Route
                      path="procurement/procurement-planning/settings/ppmp-items"
                      element={
                        <RequirePermission permission="ppmp-items.read">
                          <PpmpItemsIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="procurement/procurement-planning/settings/ppmp-items/:id"
                      element={
                        <RequirePermission permission="ppmp-items.read">
                          <PpmpItemProfilePage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      WHICH OFFICES MAY FILE — a module reached only through this Settings menu,
                      so its path NESTS under it: the path has to say where the reader came
                      from, or the breadcrumb cannot offer a way back.
                    */}
                    <Route
                      path="procurement/procurement-planning/settings/ppmp-departments"
                      element={
                        <RequirePermission permission="ppmp-departments.read">
                          <PpmpDepartmentsIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="procurement/procurement-planning/settings/ppmp-departments/:id"
                      element={
                        <RequirePermission permission="ppmp-departments.read">
                          <PpmpDepartmentProfilePage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      THE ANNUAL PROCUREMENT PLAN — the group's first work surface, and the
                      container every PPMP is built under. Gated TWICE with the same key, here
                      and on its rail entry; either alone is a defect.
                    */}
                    <Route
                      path="procurement/procurement-planning/annual-procurement-plans"
                      element={
                        <RequirePermission permission="annual-procurement-plans.read">
                          <AnnualProcurementPlansIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="procurement/procurement-planning/annual-procurement-plans/:id"
                      element={
                        <RequirePermission permission="annual-procurement-plans.read">
                          <AnnualProcurementPlanProfilePage />
                        </RequirePermission>
                      }
                    />
                    {/*
                      ONE DEPARTMENT'S PPMPs under one plan. A ROUTE rather than a drill-down
                      inside the roster panel, so it can be linked, bookmarked, opened in a
                      second tab and returned to with the browser's own Back.

                      It carries `ppmps.read` — the key of what it OPENS — where the roster
                      above it carries the PLAN's, because that roster is part of reading a plan.
                    */}
                    <Route
                      path="procurement/procurement-planning/annual-procurement-plans/:planId/departments/:orgUnitId"
                      element={
                        <RequirePermission permission="ppmps.read">
                          <AppDepartmentPage />
                        </RequirePermission>
                      }
                    />
                    {/*
                      THE PPMP's own profile. Like the PPA one suite along, this module has a
                      ROUTE and no rail row: every LIST of PPMPs is scoped to a plan and a
                      department, so a flat list of every PPMP answers nobody's question.
                    */}
                    <Route
                      path="procurement/procurement-planning/ppmps/:id"
                      element={
                        <RequirePermission permission="ppmps.read">
                          <PpmpProfilePage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      PROCUREMENT › PURCHASING — the buying cycle, and the suite's third group.

                      Product call, 29 Sep 2026. The source keeps purchase requests inside its
                      Logistics module; here the buying cycle is its own group under Procurement,
                      beside the budget cycle rather than inside it.

                      The group's bare path redirects to its first child rather than rendering a
                      blank branch route — a bookmark on it has to land somewhere.
                    */}
                    <Route
                      path="procurement/purchasing"
                      element={
                        <Navigate to="/procurement/purchasing/purchase-requests" replace />
                      }
                    />
                    <Route
                      path="procurement/purchasing/purchase-requests"
                      element={
                        <RequirePermission permission="purchase-requests.read">
                          <PurchaseRequestsIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="procurement/purchasing/purchase-requests/:id"
                      element={
                        <RequirePermission permission="purchase-requests.read">
                          <PurchaseRequestProfilePage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      GENERAL SERVICES › BUILDING MANAGEMENT — one module's surfaces, nested under
                      its own path. The bare paths REDIRECT to the monitor, so a bookmark lands
                      somewhere. Settings is gated on ANY of its rows' keys; each row's page on its
                      own.
                    */}
                    <Route
                      path="general-services"
                      element={<Navigate to="/general-services/building-management/maintenance" replace />}
                    />
                    <Route
                      path="general-services/building-management"
                      element={<Navigate to="/general-services/building-management/maintenance" replace />}
                    />
                    <Route
                      path="general-services/building-management/maintenance"
                      element={
                        <RequirePermission permission="building-management.read">
                          <MaintenanceIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="general-services/building-management/maintenance/:id"
                      element={
                        <RequirePermission permission="building-management.read">
                          <MaintenanceItemProfilePage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="general-services/building-management/calendar"
                      element={
                        <RequirePermission permission="building-management.read">
                          <MaintenanceCalendarPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="general-services/building-management/settings"
                      element={
                        <RequirePermission anyOf={['maintenance-categories.read', 'building-management.read']}>
                          <BuildingManagementSettingsPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="general-services/building-management/settings/categories"
                      element={
                        <RequirePermission permission="maintenance-categories.read">
                          <MaintenanceCategoriesIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="general-services/building-management/settings/categories/:id"
                      element={
                        <RequirePermission permission="maintenance-categories.read">
                          <MaintenanceCategoryProfilePage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="general-services/building-management/settings/aging"
                      element={
                        <RequirePermission permission="building-management.read">
                          <AgingSettingsPage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      LEARNING & DEVELOPMENT › SURVEY. The bare suite path REDIRECTS to the list,
                      so a bookmark lands somewhere. Settings sits at suite level beside the
                      Survey leaf rather than under its path: the rail lights an entry by PREFIX,
                      so `/learning-development/survey/settings` would light Survey too.

                      Settings carries Survey's own read while it has no rows of its own. A row
                      added there owes its key to this `RequirePermission` (as `anyOf`) and to the
                      sidebar entry in the same change.
                    */}
                    <Route
                      path="learning-development"
                      element={<Navigate to="/learning-development/survey" replace />}
                    />
                    <Route
                      path="learning-development/survey"
                      element={
                        <RequirePermission permission="surveys.read">
                          <SurveysIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="learning-development/survey/:id"
                      element={
                        <RequirePermission permission="surveys.read">
                          <SurveyProfilePage />
                        </RequirePermission>
                      }
                    />
                    {/*
                      One training recommended on a development plan — its profile tracks its
                      approval. Under its plan's own path: `/learning-development/training-recommendation`
                      is the register's, below, on that module's key.
                    */}
                    <Route
                      path="learning-development/survey/:surveyId/plan/:planId/training-recommendation/:id"
                      element={
                        <RequirePermission permission="surveys.read">
                          <SurveyTrainingRecommendationProfilePage />
                        </RequirePermission>
                      }
                    />
                    {/* A development plan created for several of a survey's assessed employees. */}
                    <Route
                      path="learning-development/survey/:surveyId/plan/:planId"
                      element={
                        <RequirePermission permission="surveys.read">
                          <SurveyPlanProfilePage />
                        </RequirePermission>
                      }
                    />
                    {/*
                      LEARNING & DEVELOPMENT › DEVELOPMENT PLAN — its own module and its own read,
                      beside Survey rather than under its path for the same prefix reason. The
                      register lists PLANS; `:planId` opens one (Info · Personnel), and an employee
                      under its Personnel opens their OWN copy, whose `:id` is their response's.
                    */}
                    <Route
                      path="learning-development/development-plan"
                      element={
                        <RequirePermission permission="development-plans.read">
                          <DevelopmentPlansIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="learning-development/development-plan/:planId"
                      element={
                        <RequirePermission permission="development-plans.read">
                          <SurveyPlanProfilePage from="development-plans" />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="learning-development/development-plan/:planId/personnel/:id"
                      element={
                        <RequirePermission permission="development-plans.read">
                          <DevelopmentPlanProfilePage />
                        </RequirePermission>
                      }
                    />
                    {/*
                      LEARNING & DEVELOPMENT › TRAINING RECOMMENDATIONS — its own module and its
                      own read, beside Survey and Development Plan. The register lists every plan's
                      training recommendations; `:id` opens one — the same profile a plan's own tab
                      opens under Survey, here on this module's key.
                    */}
                    <Route
                      path="learning-development/training-recommendation"
                      element={
                        <RequirePermission permission="training-recommendations.read">
                          <TrainingRecommendationsIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="learning-development/training-recommendation/:id"
                      element={
                        <RequirePermission permission="training-recommendations.read">
                          <SurveyTrainingRecommendationProfilePage from="training-recommendations" />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="learning-development/settings"
                      element={
                        <RequirePermission permission="surveys.read">
                          <SurveySettingsPage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      PUBLIC RELATIONS › PRESS RELEASE. Every route carries the module's read key.
                      The bare suite path REDIRECTS to the register. Post Operation Report's old
                      paths redirect too, so a bookmark lands on the register rather than nowhere.
                    */}
                    <Route path="public-relations" element={<Navigate to="/public-relations/press-releases" replace />} />
                    <Route
                      path="public-relations/post-operation-reports/*"
                      element={<Navigate to="/public-relations/press-releases" replace />}
                    />
                    <Route
                      path="public-relations/press-releases"
                      element={
                        <RequirePermission permission="press-releases.read">
                          <PressReleasesIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="public-relations/press-releases/:id"
                      element={
                        <RequirePermission permission="press-releases.read">
                          <PressReleaseProfilePage />
                        </RequirePermission>
                      }
                    />
                    {/* PUBLIC RELATIONS › PRESS CON — a second leaf. Both routes carry its read key. */}
                    <Route
                      path="public-relations/press-conferences"
                      element={
                        <RequirePermission permission="press-conferences.read">
                          <PressConferencesIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="public-relations/press-conferences/:id"
                      element={
                        <RequirePermission permission="press-conferences.read">
                          <PressConferenceProfilePage />
                        </RequirePermission>
                      }
                    />
                    {/*
                      Public Relations › Settings sits at SUITE level beside the Press Release leaf, not
                      under its path: the rail lights an entry by PREFIX. Its one row is Channels, so the
                      route carries that catalog's key; a row added owes its key here as `anyOf`.
                    */}
                    <Route
                      path="public-relations/settings"
                      element={
                        <RequirePermission permission="press-release-channels.read">
                          <PublicRelationsSettingsPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="public-relations/settings/channels"
                      element={
                        <RequirePermission permission="press-release-channels.read">
                          <ChannelsIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="public-relations/settings/channels/:id"
                      element={
                        <RequirePermission permission="press-release-channels.read">
                          <ChannelProfilePage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      SELF SERVICE — the requester's own surface over requisition slips. The
                      entity is Logistics-owned; this route and its keys are the surface over
                      it, which is why the path says `self-service` and the folder does too.

                      The bare group path REDIRECTS rather than rendering a blank branch — a
                      bookmark has to land somewhere.
                    */}
                    <Route
                      path="self-service"
                      element={<Navigate to="/self-service/requisition-slips" replace />}
                    />
                    <Route
                      path="self-service/requisition-slips"
                      element={
                        <RequirePermission permission="requisition-slips.read">
                          <RequisitionSlipsIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="self-service/requisition-slips/:id"
                      element={
                        <RequirePermission permission="requisition-slips.read">
                          <RequisitionSlipProfilePage />
                        </RequirePermission>
                      }
                    />
                    {/* EVIDENCE REQUESTS — the requester's own; the rows are Evidence Management's. */}
                    <Route
                      path="self-service/evidence-requests"
                      element={
                        <RequirePermission permission="evidence-requests.read">
                          <EvidenceRequestsIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="self-service/disbursement-requests"
                      element={
                        <RequirePermission permission="my-disbursement-requests.read">
                          <MyDisbursementRequestsIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="self-service/disbursement-requests/:id"
                      element={
                        <RequirePermission permission="my-disbursement-requests.read">
                          <MyDisbursementRequestProfilePage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="self-service/evidence-requests/:id"
                      element={
                        <RequirePermission permission="evidence-requests.read">
                          <EvidenceRequestProfilePage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      CASE MANAGEMENT — the case registry. Its key opens no case on its own: the
                      server also requires membership, employment and clearance.
                    */}
                    <Route
                      path="cases"
                      element={
                        <RequirePermission permission="cases.read">
                          <CasesIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="cases/:id"
                      element={
                        <RequirePermission permission="cases.read">
                          <CaseProfilePage />
                        </RequirePermission>
                      }
                    />
                    {/* A report is reached THROUGH its case: its own read key, and the case decides the rest. */}
                    <Route
                      path="cases/:caseId/reports/:reportId"
                      element={
                        <RequirePermission permission="post-operation-reports.read">
                          <PostOperationReportProfilePage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      SELF SERVICE › LEARNING & DEVELOPMENT › SURVEY — the employee's surface over
                      Learning & Development's surveys, on its own keys. The group groups
                      navigation, not routes, so the path is the module's own.
                    */}
                    <Route
                      path="self-service/surveys"
                      element={
                        <RequirePermission permission="my-surveys.read">
                          <MySurveysIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="self-service/surveys/:id"
                      element={
                        <RequirePermission permission="my-surveys.read">
                          <MySurveyProfilePage />
                        </RequirePermission>
                      }
                    />
                    {/*
                      ONE training recommendation sent to the employee — its own profile, nested
                      under the survey it came from: the survey's Preview table is the only way
                      to it, so the path says where the reader came from.
                    */}
                    <Route
                      path="self-service/surveys/:id/training-recommendations/:recommendationId"
                      element={
                        <RequirePermission permission="my-surveys.read">
                          <MySurveyTrainingRecommendationProfilePage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      SELF SERVICE › LEARNING & DEVELOPMENT › TRAINING RECOMMENDATIONS — its own
                      module and its own read, beside Survey. The register lists every training
                      recommendation sent to the employee, across every survey; `:recommendationId`
                      opens one — the same profile a survey's own table opens, here on this
                      module's key and with no survey in the path.
                    */}
                    <Route
                      path="self-service/training-recommendations"
                      element={
                        <RequirePermission permission="my-training-recommendations.read">
                          <MyTrainingRecommendationsIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="self-service/training-recommendations/:recommendationId"
                      element={
                        <RequirePermission permission="my-training-recommendations.read">
                          <MySurveyTrainingRecommendationProfilePage from="training-recommendations" />
                        </RequirePermission>
                      }
                    />

                    {/*
                      EVIDENCE MANAGEMENT. Each route carries its own module's read key; the
                      Settings container carries ANY of its rows' keys. A key opens no case on
                      its own — the server also requires membership, employment and clearance,
                      and a page refused by those says so rather than redirecting.
                    */}
                    <Route path="evidence" element={<Navigate to="/evidence/cases" replace />} />
                    <Route
                      path="evidence/cases"
                      element={
                        <RequirePermission permission="evidence-items.read">
                          <EvidenceCasesIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="evidence/cases/:id"
                      element={
                        <RequirePermission permission="evidence-items.read">
                          <EvidenceCaseProfilePage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="evidence/items/:id"
                      element={
                        <RequirePermission permission="evidence-items.read">
                          <EvidenceItemProfilePage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="evidence/settings"
                      element={
                        <RequirePermission
                          anyOf={[
                            'evidence-storage-locations.read',
                            'evidence-kinds.read',
                            'evidence-types.read',
                            'evidence-disposal-types.read',
                            'evidence-file-types.read',
                          ]}
                        >
                          <EvidenceSettingsPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="evidence/settings/storage-locations"
                      element={
                        <RequirePermission permission="evidence-storage-locations.read">
                          <EvidenceStorageLocationsIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="evidence/settings/storage-locations/:id"
                      element={
                        <RequirePermission permission="evidence-storage-locations.read">
                          <EvidenceStorageLocationProfilePage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="evidence/settings/kinds"
                      element={
                        <RequirePermission permission="evidence-kinds.read">
                          <EvidenceKindsIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="evidence/settings/kinds/:id"
                      element={
                        <RequirePermission permission="evidence-kinds.read">
                          <EvidenceKindProfilePage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="evidence/settings/types"
                      element={
                        <RequirePermission permission="evidence-types.read">
                          <EvidenceTypesIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="evidence/settings/types/:id"
                      element={
                        <RequirePermission permission="evidence-types.read">
                          <EvidenceTypeProfilePage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="evidence/settings/disposal-types"
                      element={
                        <RequirePermission permission="evidence-disposal-types.read">
                          <EvidenceDisposalTypesIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="evidence/settings/disposal-types/:id"
                      element={
                        <RequirePermission permission="evidence-disposal-types.read">
                          <EvidenceDisposalTypeProfilePage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="evidence/settings/file-types"
                      element={
                        <RequirePermission permission="evidence-file-types.read">
                          <EvidenceFileTypesPage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      PURCHASE ORDERS — the group's second work surface, appended after the
                      requests rather than slotted in: a request is raised, approved, and then
                      ordered against, which is also the order a reader learns them in.
                    */}
                    <Route
                      path="procurement/purchasing/purchase-orders"
                      element={
                        <RequirePermission permission="purchase-orders.read">
                          <PurchaseOrdersIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="procurement/purchasing/purchase-orders/:id"
                      element={
                        <RequirePermission permission="purchase-orders.read">
                          <PurchaseOrderProfilePage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      THE GROUP'S SETTINGS, and its one row today. A module reached THROUGH
                      another module's Settings menu nests its path, because that menu is the
                      only way to it — the path has to say where the reader came from, or the
                      breadcrumb cannot offer a way back.
                    */}
                    <Route
                      path="procurement/purchasing/settings"
                      element={
                        <RequirePermission anyOf={['suppliers.read']}>
                          <PurchasingSettingsPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="procurement/purchasing/settings/suppliers"
                      element={
                        <RequirePermission permission="suppliers.read">
                          <SuppliersIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="procurement/purchasing/settings/suppliers/:id"
                      element={
                        <RequirePermission permission="suppliers.read">
                          <SupplierProfilePage />
                        </RequirePermission>
                      }
                    />

                    {/*
                      CASH DIVISION. The bare suite path redirects to its work surface, so a
                      bookmark on it lands somewhere. The two catalog pages nest under Settings,
                      the menu that is their only way in, and are built from the same descriptor
                      as the menu rows — a vocabulary cannot appear in one without the other.
                    */}
                    <Route path="cash-division" element={<Navigate to="/cash-division/requests" replace />} />
                    <Route
                      path="cash-division/requests"
                      element={
                        <RequirePermission permission="disbursement-requests.read">
                          <DisbursementRequestsIndexPage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="cash-division/requests/:id"
                      element={
                        <RequirePermission permission="disbursement-requests.read">
                          <DisbursementRequestProfilePage />
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="cash-division/settings"
                      element={
                        <RequirePermission anyOf={['cash-catalogs.read']}>
                          <CashDivisionSettingsPage />
                        </RequirePermission>
                      }
                    />
                    {CASH_CATALOGS.map((catalog) => (
                      <Route key={catalog.slug} path={catalog.settingsPath.slice(1)}>
                        <Route
                          index
                          element={
                            <RequirePermission permission="cash-catalogs.read">
                              <CashCatalogIndexPage catalog={catalog} />
                            </RequirePermission>
                          }
                        />
                        <Route
                          path=":id"
                          element={
                            <RequirePermission permission="cash-catalogs.read">
                              <CashCatalogProfilePage catalog={catalog} />
                            </RequirePermission>
                          }
                        />
                      </Route>
                    ))}

                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </DashboardLayout>
              </ProtectedRoute>
            }
          />
        </Routes>
        </RepositoryUploadProvider>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

/**
 * Sends the user to the first module they can actually see. Landing on a page they lack
 * permission for and bouncing back would read as the app being broken.
 */
function LandingRedirect() {
  const { hasPermission } = useAuth();

  /*
    Asked of the sidebar config, which already knows every module, its route and its key.
    The previous version tested `users.read` alone, so an account granted only
    `personnel.read` was told it had access to no modules while the rail beside it listed
    Personnel — two answers to one question, from two places that both claimed to know.
  */
  const destination = firstVisibleRoute(hasPermission);

  if (destination) {
    return <Navigate to={destination} replace />;
  }

  return (
    <div className="flex h-full items-center justify-center px-6 text-center">
      <div>
        <p className="text-sm text-slate-600">You do not have access to any modules yet.</p>
        <p className="mt-1 text-xs text-slate-400">
          Ask an administrator to grant your role the permissions you need.
        </p>
      </div>
    </div>
  );
}

/**
 * `/inventory/items/:id` → `/inventory/settings/items/:id`, keeping the record.
 *
 * A component rather than a `<Navigate>` because the destination carries a route parameter,
 * which the element cannot interpolate on its own. The retired Storage Facilities profile
 * redirect drops its id and lands on the list; this one does not, because a bookmarked item —
 * or a Stock Levels anchor followed before this change — should arrive at the record the
 * reader asked for rather than at a list with no explanation.
 */
function RedirectItemProfile() {
  const { id } = useParams<{ id: string }>();

  return <Navigate to={id ? `/inventory/settings/items/${id}` : '/inventory/settings/items'} replace />;
}

function NotFound() {
  return (
    <div className="flex h-full items-center justify-center px-6 text-center">
      <div>
        <p className="text-sm text-slate-600">That page does not exist.</p>
        <p className="mt-1 text-xs text-slate-400">Check the address, or use the sidebar.</p>
      </div>
    </div>
  );
}
